/**
 * Accessibility utilities for improving website accessibility
 * Provides helper functions for ARIA attributes, keyboard navigation, and focus management
 */
import { useEffect, useRef, useState } from 'react';

/**
 * Hook to manage focus trap within a component (for modals, dialogs, etc.)
 * @param isActive Whether the focus trap is active
 * @param onEscape Callback when Escape key is pressed
 */
export const useFocusTrap = (isActive: boolean, onEscape?: () => void) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        onEscape();
        return;
      }

      if (e.key !== 'Tab') return;

      // Trap focus in the container
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Focus the first element when the trap becomes active
    if (firstElement) {
      firstElement.focus();
    }

    // Add event listener for keydown
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, onEscape]);

  return containerRef;
};

/**
 * Hook to manage skip links for keyboard navigation
 */
export const useSkipLink = () => {
  const [isVisible, setIsVisible] = useState(false);

  const skipLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Tab' && !e.shiftKey && !isVisible) {
        setIsVisible(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible]);

  return {
    skipLinkRef,
    isVisible,
    setIsVisible,
  };
};

/**
 * Helper function to generate ARIA attributes for common components
 */
export const getAriaAttributes = {
  /**
   * Get ARIA attributes for accordions
   */
  accordion: (expanded: boolean, controls: string) => ({
    'aria-expanded': expanded,
    'aria-controls': controls,
    role: 'button',
  }),
  /**
   * Get ARIA attributes for buttons
   */
  button: (label: string, expanded?: boolean, controls?: string) => ({
    'aria-label': label,
    ...(expanded !== undefined && { 'aria-expanded': expanded }),
    ...(controls && { 'aria-controls': controls }),
  }),

  /**
   * Get ARIA attributes for tabs
   */
  tab: (selected: boolean, controls: string) => ({
    role: 'tab',
    'aria-selected': selected,
    'aria-controls': controls,
    tabIndex: selected ? 0 : -1,
  }),

  /**
   * Get ARIA attributes for tab panels
   */
  tabPanel: (id: string, labelledBy: string) => ({
    role: 'tabpanel',
    id,
    'aria-labelledby': labelledBy,
    tabIndex: 0,
  }),

  /* Accordion attributes are already defined above */
};

/**
 * Hook to manage keyboard navigation for interactive elements
 */
export const useKeyboardNavigation = (onKeyAction: Record<string, () => void>) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const action = onKeyAction[e.key];
    if (action) {
      e.preventDefault();
      action();
    }
  };

  return { handleKeyDown };
};

/**
 * Hook to announce messages to screen readers
 */
export const useAnnouncer = () => {
  const [message, setMessage] = useState('');
  const announcerRef = useRef<HTMLDivElement>(null);

  const announce = (text: string, priority: 'polite' | 'assertive' = 'polite') => {
    setMessage(text);

    if (announcerRef.current) {
      announcerRef.current.setAttribute('aria-live', priority);
    }

    // Clear the message after it's been announced
    setTimeout(() => {
      setMessage('');
    }, 1000);
  };

  return {
    announce,
    announcerRef,
    message,
  };
};

/**
 * Helper function to check color contrast ratio
 * @param foreground Foreground color in hex format (e.g., #ffffff)
 * @param background Background color in hex format (e.g., #000000)
 * @returns Contrast ratio (WCAG recommends at least 4.5:1 for normal text)
 */
/**
 * Export ariaAttributes as an alias for getAriaAttributes for backward compatibility
 */
export const ariaAttributes = getAriaAttributes;

/**
 * Helper function to check color contrast ratio
 * @param foreground Foreground color in hex format (e.g., #ffffff)
 * @param background Background color in hex format (e.g., #000000)
 * @returns Contrast ratio (WCAG recommends at least 4.5:1 for normal text)
 */
export const getContrastRatio = (foreground: string, background: string): number => {
  // Convert hex to RGB
  const hexToRgb = (hex: string): number[] => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);

    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [0, 0, 0];
  };

  // Calculate relative luminance
  const getLuminance = (rgb: number[]): number => {
    const [r, g, b] = rgb.map(c => {
      const channel = c / 255;
      return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const foregroundRgb = hexToRgb(foreground);
  const backgroundRgb = hexToRgb(background);

  const foregroundLuminance = getLuminance(foregroundRgb);
  const backgroundLuminance = getLuminance(backgroundRgb);

  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};
